import { EmblaCarousel, emblaPrevNextBtnsClickHandlers, emblaDotBtnsAndClickHandlers, Autoplay, Fade } from "@utilities/revyrie-bundle";
import { parseConfig, getLazyLoadComponentObserver } from '@utilities/theme';

/**
 * Custom HTML element that wraps the Embla carousel functionality.
 * Provides lazy loading, navigation buttons, and dot indicators for carousel slides.
 *
 * @class EmblaSlider
 * @extends HTMLElement
 *
 * @example
 * <embla-slider class="lazy-load debug" data-options='{"loop": true}'>
 *   <div class="embla__viewport">
 *     <div class="embla__container">
 *       <div class="embla__slide">Slide 1</div>
 *       <div class="embla__slide">Slide 2</div>
 *     </div>
 *   </div>
 *   <button class="embla__button--prev">Previous</button>
 *   <button class="embla__button--next">Next</button>
 *   <div class="embla__dots"></div>
 * </embla-slider>
 */
class EmblaSlider extends HTMLElement {
  /**
   * Creates an instance of EmblaSlider.
   * Initializes lazy loading and parses configuration options from data attributes.
   *
   * @constructor
   */
  constructor() {
    super();
    /** @type {boolean} Whether the slider should be lazy loaded */
    this.lazyLoad = this.classList.contains('lazy-load');
    /** @type {Object} Parsed configuration options for the Embla carousel */
    this.config = parseConfig(this, this.allowDebug());
    this.emblaApi = null;
    if(this.allowDebug()) {
      console.log(`EmblaSlider options:`, this.config.options);
    }
  }

  /**
   * Checks if debug mode is enabled for this slider instance.
   * Debug mode is activated by adding the 'debug' class to the element.
   *
   * @returns {boolean} True if debug mode is enabled
   */
  allowDebug() {
    return this.classList.contains('debug');
  }

  isEmblaDisabled() {
    return this.config?.options?.active === false || this.hasAttribute('data-embla-disabled');
  }

  isFadeCarousel() {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (isDesktop) {
      return (
        this.classList.contains('embla--fade') ||
        this.classList.contains('embla--fade-desktop')
      );
    }
    return (
      this.classList.contains('embla--fade') ||
      this.classList.contains('embla--fade-mobile')
    );
  }

  isHeroCarousel() {
    return this.classList.contains('embla--hero-carousel');
  }

  getEmblaOptions() {
    const options = { ...(this.config.options || {}) };

    if (this.isHeroCarousel()) {
      options.dragFree = false;
      if (!options.loop) {
        options.containScroll = options.containScroll ?? 'trimSnaps';
      }
    }

    return options;
  }

  /**
   * Builds the Embla plugin instances requested by the component-config JSON.
   * Each entry in `config.plugins` is `{ name, options }`; recognized names are mapped to
   * their imported plugin factory. Unknown names are ignored so config can stay forward-compatible.
   *
   * @returns {Array<object>} instantiated Embla plugins (empty if none requested)
   */
  buildPlugins() {
    const requested = this.config.plugins;
    if (!Array.isArray(requested) || requested.length === 0) {
      return [];
    }
    const factories = {
      Autoplay,
      Fade,
    };
    const plugins = [];
    for (const plugin of requested) {
      const factory = factories[plugin?.name];
      if (!factory) {
        if (this.allowDebug()) {
          console.log(`EmblaSlider skipping unknown plugin:`, plugin?.name);
        }
        continue;
      }
      plugins.push(factory(plugin.options || {}));
    }
    return plugins;
  }

  bindDragSmoothing(emblaApi) {
    const setDragging = (isDragging) => {
      this.classList.toggle('embla--dragging', isDragging);
      const container = this.querySelector('.embla__container');
      if (container) {
        container.style.willChange = isDragging ? 'transform' : '';
      }
    };

    const snapHeroToNearestSlide = () => {
      if (!this.isHeroCarousel()) {
        return;
      }
      emblaApi.scrollTo(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('pointerDown', () => setDragging(true));
    emblaApi.on('pointerUp', () => {
      setDragging(false);
      snapHeroToNearestSlide();
    });
    emblaApi.on('destroy', () => setDragging(false));
  }

  /**
   * Selector for natively-focusable descendants we need to manage on hidden slides.
   * @returns {string}
   */
  get focusableSelector() {
    return 'a[href], button, input, select, textarea, [tabindex], iframe, [contenteditable]';
  }

  /**
   * Set tabindex="-1" on focusable descendants of an out-of-view slide (remembering the
   * prior value), or restore the previous tabindex when the slide comes back into view.
   *
   * @param {HTMLElement} slide The slide node to update.
   * @param {boolean} focusable Whether the slide's focusables should be tabbable.
   */
  setSlideFocusable(slide, focusable) {
    const elements = slide.querySelectorAll(this.focusableSelector);
    elements.forEach((el) => {
      if (focusable) {
        // restore: only act on elements we previously modified
        if (el.hasAttribute('data-prev-tabindex')) {
          const prev = el.getAttribute('data-prev-tabindex');
          if (prev === '') {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', prev);
          }
          el.removeAttribute('data-prev-tabindex');
        }
      } else {
        // hide: remember the original tabindex once, then make untabbable
        if (!el.hasAttribute('data-prev-tabindex')) {
          el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') ?? '');
        }
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Keep out-of-view slides out of the tab order. Embla reports the indices currently in
   * view via slidesInView() (which can be more than one), so any slide whose index is not
   * in that set has its focusable descendants made untabbable.
   */
  updateSlideTabIndexes() {
    if (!this.emblaApi) {
      return;
    }
    const inView = this.emblaApi.slidesInView();
    this.emblaApi.slideNodes().forEach((slide, index) => {
      this.setSlideFocusable(slide, inView.includes(index));
    });
  }

  /**
   * Called when the element is added to the DOM.
   * Determines whether to initialize immediately or set up lazy loading.
   */
  connectedCallback() {
    if (this.isEmblaDisabled()) {
      if (this.allowDebug()) {
        console.log('EmblaSlider is disabled and will not initialize');
      }
      return;
    }

    if (this.lazyLoad) {
      if(this.allowDebug()) {
        console.log(`EmblaSlider is lazy-loading`);
      }
      getLazyLoadComponentObserver().observe(this);
    } else {
      if(this.allowDebug()) {
        console.log(`EmblaSlider is ready to initialize`);
      }
      this.init();
    }
  }

  /**
   * Initializes the Embla carousel with navigation controls and dot indicators.
   * Sets up event handlers for previous/next buttons and dot navigation.
   * Configures cleanup handlers to prevent memory leaks when the carousel is destroyed.
   */
  init() {
    if (this.isEmblaDisabled()) {
      return;
    }

    if (this.emblaApi) {
      this.emblaApi.reInit();
      return;
    }
    // elements
    const viewportNode = this.querySelector('.embla__viewport');
    const prevBtnNode = this.querySelector('.embla__button--prev');
    const nextBtnNode = this.querySelector('.embla__button--next');
    const dotsNode = this.querySelector('.embla__dots');
    const emblaOptions = this.getEmblaOptions();
    const plugins = this.buildPlugins();
    if (this.allowDebug()) {
      console.log('EmblaSlider plugins:', plugins.map(plugin => plugin.name));
    }
    // initialize embla carousel
    const emblaApi = plugins.length
      ? EmblaCarousel(viewportNode, emblaOptions, plugins)
      : EmblaCarousel(viewportNode, emblaOptions);
    this.emblaApi = emblaApi;
    this.bindDragSmoothing(emblaApi);
    // embla plugins
    if(prevBtnNode && nextBtnNode) {
      const removePrevNextBtnsClickHandlers = emblaPrevNextBtnsClickHandlers(
        emblaApi,
        prevBtnNode,
        nextBtnNode
      );
      // cleanup
      emblaApi.on('destroy', removePrevNextBtnsClickHandlers);
    }
    if(dotsNode) {
      const removeDotBtnsAndClickHandlers = emblaDotBtnsAndClickHandlers(
        emblaApi,
        dotsNode
      );
      // cleanup
      emblaApi.on('destroy', removeDotBtnsAndClickHandlers);
    }

    if (this.isFadeCarousel()) {
      let fadeSyncFrame = 0;
      const applyFadeSlideState = () => {
        const selectedIndex = emblaApi.selectedScrollSnap();
        emblaApi.slideNodes().forEach((slide, index) => {
          slide.classList.toggle('embla__slide--active', index === selectedIndex);
        });
      };
      const syncFadeSlides = (immediate = false) => {
        if (immediate) {
          applyFadeSlideState();
          return;
        }
        if (fadeSyncFrame) {
          return;
        }
        fadeSyncFrame = requestAnimationFrame(() => {
          fadeSyncFrame = 0;
          applyFadeSlideState();
        });
      };
      emblaApi
        .on('init', () => syncFadeSlides(true))
        .on('reInit', () => syncFadeSlides(true))
        .on('select', syncFadeSlides)
        .on('scroll', syncFadeSlides);
      emblaApi.on('destroy', () => {
        if (fadeSyncFrame) {
          cancelAnimationFrame(fadeSyncFrame);
        }
        emblaApi.slideNodes().forEach((slide) => slide.classList.remove('embla__slide--active'));
      });
    } else {
      emblaApi.slideNodes().forEach((slide) => slide.classList.remove('embla__slide--active'));
    }

    // keep out-of-view slides out of the tab order on change/reInit, and whenever
    // Embla's IntersectionObserver reports slidesInView() has changed (including its
    // first report after init, which lands asynchronously — calling this synchronously
    // right after construction runs against an empty/unset slidesInView()).
    emblaApi.on('select', () => this.updateSlideTabIndexes());
    emblaApi.on('reInit', () => this.updateSlideTabIndexes());
    emblaApi.on('slidesInView', () => this.updateSlideTabIndexes());
  }

  refresh() {
    if (this.isEmblaDisabled()) {
      return;
    }

    if (this.emblaApi) {
      this.emblaApi.reInit();
    } else {
      this.init();
    }
  }
}

if(!customElements.get('embla-slider')) {
  customElements.define('embla-slider', EmblaSlider);
}
